/**
 * Sometimes we want to persist simple pieces of data for which IndexedDB would be overkill.
 * This provides an interface to window.localStorage but does not use the angular-cache package and all its
 * compression/decompression overhead. This should not be used for large amounts of data, it is intended for simple
 * strings or similar.
 *
 * @constructor
 */
const LocalStorageDataStore = function () {
    /* istanbul ignore next */
    /**
     * Define localStorage as a property to make this testable.
     *
     * @type {Storage}
     */
    this.localStorage = typeof window !== 'undefined' ? window.localStorage : undefined;
};

LocalStorageDataStore.prototype = {
    /**
     * @param {string} key
     * @param {object} data
     */
    persist(key, data) {
        this.localStorage.setItem(key, JSON.stringify(data));
    },

    /**
     * @param {string} key
     *
     * @return {?object}
     */
    find(key) {
        return JSON.parse(this.localStorage.getItem(key));
    },

    /**
     * Returns a value from local storage if it exists, and removes it from storage.
     *
     * @param {string} key
     */
    pull(key) {
        let value = this.find(key);
        this.remove(key);
        return value;
    },

    /**
     * @param {string} key
     */
    remove(key) {
        this.localStorage.removeItem(key);
    },

    /**
     * @param {string} prefix
     */
    removeByPrefix(prefix) {
        this.getKeys().forEach((key) => {
            if (key.indexOf(prefix) === 0) {
                this.remove(key);
            }
        });
    },

    /**
     * Return the keys of all items in local storage.
     *
     * @return {string[]}
     */
    getKeys() {
        return Object.keys(this.localStorage);
    },

    /**
     * Clean old items from localStorage that should no longer be there.
     * (Old angular-cache keys)
     */
    clean() {
        // This is a list of things that used to be stored in localstorage using angular-cache.
        // They have been moved to IndexedDB or are otherwise no longer needed. Remove any lingering localstorage
        // entries.
        // Eventually we can change this to a whitelist instead of a blacklist (are we still allowed to call it that?)
        const keysToRemove = [
            'angular-cache',
            'DB.', // LargeVolumeStorageImplementation created these (for OrderCache).
            'OrderCache.', // A really old structure for the order cache.
        ];

        const shouldCleanKey = (key) => {
            for (let i = 0; i < keysToRemove.length; i++) {
                let keyToRemove = keysToRemove[i];
                if (key.indexOf(keyToRemove) === 0) {
                    return true;
                }
            }

            return false;
        };

        this.getKeys().forEach((key) => {
            if (shouldCleanKey(key)) {
                console.warn(`Deleting old data from local storage: ${key}`);
                this.remove(key);
            }
        });
    },
};

module.exports = LocalStorageDataStore;
